import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { can, type Permission, type RealtimeEvent, type SessionDto, type WorkspaceSettingsDto, type WorkspaceSummaryDto } from '@onesource/shared';
import { api, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/display';

export const sessionKey = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async () => {
      try {
        return await api.get<SessionDto>('/api/auth/session');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

function FullPageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <Skeleton className="h-6 w-32" />
    </div>
  );
}

/** Routes that require a signed-in user. */
export function RequireAuth() {
  const { data, isLoading } = useSession();
  const location = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (!data) return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <Outlet />;
}

/** Sends signed-in users to their first workspace (or onboarding); everyone else sees `signedOut`. */
export function HomeRedirect({ signedOut }: { signedOut: ReactNode }) {
  const { data, isLoading } = useSession();
  if (isLoading) return <FullPageLoader />;
  if (!data) return signedOut;
  let last: string | null = null;
  try {
    last = localStorage.getItem('os-last-workspace');
  } catch {
    /* storage unavailable */
  }
  const target = data.workspaces.find((w) => w.id === last) ?? data.workspaces[0];
  return <Navigate to={target ? `/w/${target.id}/inbox` : '/onboarding'} replace />;
}

interface WorkspaceContextValue {
  workspace: WorkspaceSummaryDto;
  settings: WorkspaceSettingsDto | undefined;
  can: (permission: Permission) => boolean;
  base: string;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used inside a workspace route');
  return ctx;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { workspaceId } = useParams();
  const { data: session } = useSession();
  const workspace = session?.workspaces.find((w) => w.id === workspaceId);
  const settings = useQuery({
    queryKey: ['ws', workspaceId, 'settings'],
    queryFn: () => api.get<WorkspaceSettingsDto>(`/api/workspaces/${workspaceId}`),
    enabled: !!workspace,
  });

  useEffect(() => {
    if (workspace) {
      try {
        localStorage.setItem('os-last-workspace', workspace.id);
      } catch {
        /* ignore */
      }
    }
  }, [workspace]);

  useRealtime(workspace?.id);

  if (!session) return null;
  if (!workspace) return <Navigate to="/" replace />;
  return (
    <WorkspaceContext.Provider
      value={{ workspace, settings: settings.data, can: (p) => can(workspace.role, p), base: `/w/${workspace.id}` }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

/** Subscribes to workspace events (SSE) and refreshes affected queries. */
function useRealtime(workspaceId: string | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!workspaceId || typeof EventSource === 'undefined') return;
    const source = new EventSource(`/api/workspaces/${workspaceId}/events`, { withCredentials: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const pending = new Set<string>();
    const flush = () => {
      for (const key of pending) void qc.invalidateQueries({ queryKey: ['ws', workspaceId, key] });
      pending.clear();
    };
    const onEvent = (e: MessageEvent) => {
      try {
        const event = JSON.parse(e.data) as RealtimeEvent;
        if (event.type.startsWith('lead')) ['leads', 'lead', 'overview', 'customer'].forEach((k) => pending.add(k));
        if (event.type.startsWith('customer')) ['customers', 'customer', 'lead'].forEach((k) => pending.add(k));
        if (event.type.startsWith('connection')) ['channels', 'overview'].forEach((k) => pending.add(k));
        if (event.type === 'notification.created') pending.add('notifications');
        if (event.type === 'follow_up.updated') ['follow-ups', 'overview'].forEach((k) => pending.add(k));
        if (event.type.startsWith('lead')) ['follow-ups', 'operations'].forEach((k) => pending.add(k));
        clearTimeout(timer);
        timer = setTimeout(flush, 250);
      } catch {
        /* ignore malformed */
      }
    };
    for (const type of ['lead.created', 'lead.updated', 'customer.updated', 'connection.updated', 'notification.created', 'follow_up.updated']) source.addEventListener(type, onEvent);
    return () => {
      clearTimeout(timer);
      source.close();
    };
  }, [workspaceId, qc]);
}
